import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui'
import { Badge } from '@/components/ui'
import { cn } from '@/utils'
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Hand,
} from 'lucide-react'
import type { AvatarSessionState } from '@/connectors/avatar'

interface AvatarControlsProps {
  sessionState: AvatarSessionState
  isMuted?: boolean
  isVideoMuted?: boolean
  onStartSession: () => void
  onStopSession: () => void
  onMuteToggle?: () => void
  onVideoMuteToggle?: () => void
  onInterrupt?: () => void
  disabled?: boolean
  compact?: boolean
  className?: string
}

const stateLabels: Record<AvatarSessionState, string> = {
  idle: 'Not Connected',
  connecting: 'Connecting...',
  connected: 'Connected',
  ready: 'Ready',
  speaking: 'Speaking',
  thinking: 'Thinking...',
  error: 'Error',
  disconnected: 'Disconnected',
}

const stateColors: Record<AvatarSessionState, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  idle: 'secondary',
  connecting: 'warning',
  connected: 'success',
  ready: 'success',
  speaking: 'success',
  thinking: 'warning',
  error: 'destructive',
  disconnected: 'secondary',
}

export function AvatarControls({
  sessionState,
  isMuted = false,
  isVideoMuted = false,
  onStartSession,
  onStopSession,
  onMuteToggle,
  onVideoMuteToggle,
  onInterrupt,
  disabled = false,
  compact = false,
  className,
}: AvatarControlsProps) {
  const isActive =
    sessionState !== 'idle' &&
    sessionState !== 'disconnected' &&
    sessionState !== 'error'
  const isSpeaking = sessionState === 'speaking'

  if (compact) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Button
          variant={isActive ? 'destructive' : 'default'}
          size="sm"
          onClick={isActive ? onStopSession : onStartSession}
          disabled={disabled || sessionState === 'connecting'}
          leftIcon={isActive ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
        >
          {isActive ? 'End' : 'Start'}
        </Button>
        {isActive && (
          <Badge variant={stateColors[sessionState]}>
            {stateLabels[sessionState]}
          </Badge>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'p-4 rounded-xl bg-card border border-border',
        className
      )}
    >
      {/* Status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'w-2 h-2 rounded-full',
              sessionState === 'error' && 'bg-destructive',
              sessionState === 'connecting' && 'bg-warning animate-pulse',
              (sessionState === 'connected' || sessionState === 'ready') &&
                'bg-success',
              sessionState === 'speaking' && 'bg-success animate-pulse',
              sessionState === 'thinking' && 'bg-warning animate-pulse',
              (sessionState === 'idle' || sessionState === 'disconnected') &&
                'bg-muted-foreground/40'
            )}
          />
          <span className="text-sm font-medium text-foreground">
            {stateLabels[sessionState]}
          </span>
        </div>

        {isActive && (
          <div className="flex items-center gap-1">
            {onMuteToggle && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onMuteToggle}
                className="w-8 h-8 p-0"
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? (
                  <MicOff className="w-4 h-4 text-destructive" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </Button>
            )}

            {onVideoMuteToggle && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onVideoMuteToggle}
                className="w-8 h-8 p-0"
                title={isVideoMuted ? 'Show video' : 'Hide video'}
              >
                {isVideoMuted ? (
                  <VolumeX className="w-4 h-4 text-destructive" />
                ) : (
                  <Volume2 className="w-4 h-4" />
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Main Controls */}
      <div className="flex items-center gap-3">
        <Button
          variant={isActive ? 'destructive' : 'default'}
          onClick={isActive ? onStopSession : onStartSession}
          disabled={disabled || sessionState === 'connecting'}
          className="flex-1"
          leftIcon={
            isActive ? (
              <VideoOff className="w-4 h-4" />
            ) : (
              <Video className="w-4 h-4" />
            )
          }
        >
          {isActive ? 'End Session' : 'Start Avatar'}
        </Button>

        {/* Interrupt button */}
        <AnimatePresence>
          {isSpeaking && onInterrupt && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
            >
              <Button
                variant="secondary"
                onClick={onInterrupt}
                leftIcon={<Hand className="w-4 h-4" />}
              >
                Interrupt
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
